// payloadRecovery.js — Reconstruct a designer payload from descriptor_json
// Used when opening legacy surveys that were saved before designer_payload_json
// was persisted. Positions are auto-laid out vertically; the user can rearrange.

const Y_SPACING = 150;
const X_CENTER = 300;

/**
 * Reconstruct a designer-compatible payload from a survey version's descriptor_json.
 *
 * @param {object} descriptor  - The descriptor_json from the survey version
 * @param {string} [surveyName] - Fallback name (from the survey list row or summary)
 * @returns {{ survey: object, nodes: Array, edges: Array } | null}
 */
export function reconstructPayloadFromDescriptor(descriptor, surveyName) {
  if (!descriptor || typeof descriptor !== 'object') return null;

  const sd = descriptor.survey_descriptor || {};
  const questions = Array.isArray(descriptor.questions) ? descriptor.questions : [];
  const flow = Array.isArray(descriptor.flow) ? descriptor.flow : [];

  if (questions.length === 0) return null; // nothing to reconstruct

  // --- Survey metadata ---
  const survey = {
    title: sd.survey_name || surveyName || 'Untitled Survey',
    description: sd.survey_purpose || '',
    estimatedDuration: sd.estimated_time || '',
    author: sd.author || '',
    version: sd.version || '1.0',
    category: sd.category || 'general',
    googleVoice: sd.voice_settings?.sam_voice || '',
    language: sd.language || 'en'
  };

  // --- Nodes (auto-layout: vertical stack) ---
  const nodes = questions.map((q, idx) => {
    let nodeType = 'question';
    if (q.question_type === 'introduction') nodeType = 'start';
    else if (q.question_type === 'completion') nodeType = 'end';
    else if (q.question_type === 'decision') nodeType = 'decision';

    return {
      id: q.question_id,
      type: nodeType,
      position: { x: X_CENTER, y: idx * Y_SPACING },
      data: {
        id: q.display_id || q.legacy_label || q.question_id,
        text: q.question_text || '',
        type: q.question_type || 'text',
        question_type: q.question_type || 'text',
        variable: q.variable_name || null,
        variable_name: q.variable_name || null,
        required: q.required !== false,
        category: q.category || 'general',
        ...(q.choices ? { choices: q.choices } : {}),
        ...(q.min_value != null ? { minValue: q.min_value } : {}),
        ...(q.max_value != null ? { maxValue: q.max_value } : {}),
        ...(q.context ? { context: q.context } : {})
      }
    };
  });

  // --- Edges from flow rules ---
  const edges = flow.map((f, idx) => {
    const edge = {
      id: `e_${f.from}_${f.to}_${idx}`,
      source: f.from,
      target: f.to
    };
    if (f.condition) {
      edge.label = f.condition;
      edge.data = { condition: f.condition, priority: f.priority || 0 };
    }
    return edge;
  });

  return { survey, nodes, edges };
}
